/*
 * Loads a Google Tag Manager container on demand and forwards dataLayer pushes
 * from the embedded widget. Unlike GA4 (gtag.js, always loaded), GTM only loads
 * when the widget sends an `init` message carrying a configured container id.
 */
import { IFRAME_URL } from '@/constants'
import { BW_GTM_EVENT } from '../../../constants'

// The widget iframe is served from IFRAME_URL, so legitimate BW_GTM_EVENT
// messages always originate from this origin. Anything else is another script
// on the customer's page and must not be allowed to load a GTM container or
// push to the dataLayer.
const WIDGET_ORIGIN = new URL(IFRAME_URL).origin

let gtmInitialized = false

function isGTMScriptSrcPresent(): boolean {
  return !!document.querySelector('script[src*="googletagmanager.com/gtm.js"]')
}

function loadGTMScript(containerId: string) {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`
  document.head.appendChild(script)
}

function initGTMContainer(containerId: string) {
  // Inject the container script at most once, even across duplicate `init`
  // messages or if another tool already loaded GTM on the page.
  if (gtmInitialized || isGTMScriptSrcPresent()) {
    gtmInitialized = true
    return
  }
  gtmInitialized = true
  loadGTMScript(containerId)
}

export function initGTMListener() {
  window.addEventListener('message', (event) => {
    if (event.origin !== WIDGET_ORIGIN) return
    if (event.data?.type !== BW_GTM_EVENT) return

    const { action, id, payload } = event.data.payload ?? {}

    if (action === 'init' && typeof id === 'string') {
      initGTMContainer(id)
      return
    }

    // Ignore pushes until a container is loaded so GA4-only sites (which never
    // send `init`) don't accumulate unread ecommerce objects in the dataLayer.
    if (action === 'push' && payload && gtmInitialized) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(payload)
    }
  })
}
